import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form"; 
import drug_img from "../../../assets/images/DrugDefault.png";
import { ExtendedSelectedDrug } from "../types/prescription.types";
import {
  GeneralInput,
  GeneralSelect,
  GeneralRadio,
  GeneralCheckbox,
  UnitInput,
} from "../../../components";
import { AppConfig } from "../../../api/config/config";

interface PrescriptionDrugItemProps {
  field: any;
  index: number;
  drug: ExtendedSelectedDrug;
  isCustomDrug: boolean;
  identifier: string | number;
  onRemove: (identifier: string | number, isCustom: boolean) => void;
  mealTypeOptions: any[];
  frequencyTypeOptions: any[];
  timesOptions: any[];
  currentLanguage: string;
  maxInstructionsLength: number;
  itemVariants: any;
}

const PrescriptionDrugItem: React.FC<PrescriptionDrugItemProps> = ({
  field,
  index,
  drug,
  isCustomDrug,
  identifier,
  onRemove,
  mealTypeOptions,
  frequencyTypeOptions,
  timesOptions,
  currentLanguage,
  maxInstructionsLength,
  itemVariants,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <motion.div
      key={field.id}
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      className="flex flex-col"
    >
      <div className="mb-4 flex w-fit items-center gap-4 rounded-lg p-3">
        <button
          type="button"
          onClick={() => onRemove(identifier, isCustomDrug)}
          className="text-4xl transition-colors hover:text-primary"
          aria-label={t("remove_drug")}
        >
          &times;
        </button>
        <img
          src={drug?.Image ? `${AppConfig.SERVER_URL}${drug.Image}` : drug_img}
          alt={drug?.DrugName || "Drug Image"}
          className="h-10 w-10 rounded-lg object-cover"
        />
        <div>
          <h3 className="font-bold">
            {isCustomDrug ? drug.Name : drug.DrugName}
          </h3>
          <p className="text-body-text line-clamp-1">
            {currentLanguage === "ar"
              ? drug?.FacilityNameAr
              : drug?.FacilityNameEn || drug?.FacilityName}
          </p>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-5">
        <div className="grid grid-cols-3 gap-3">
          {drug?.DosageForm ? (
            <UnitInput
              label={t("dose_amount_per_time")}
              name={`Drugs.${index}.DosageNum`}
              type={drug.DosageForm}
              numberOnly
            />
          ) : (
            <div className="relative">
              <UnitInput
                label={t("dose_amount_per_time")}
                placeholder={t("dose_amount_per_time")}
                name={`Drugs.${index}.DosageNum`}
                type={
                  drug?.DosageFormUnitName ??
                  drug.DosageFormName ??
                  drug?.DosageForm ??
                  ""
                }
                numberOnly
                customStyleClasses="w-full !h-[50px] !border !border-body-text !bg-white placeholder:!text-text-muted"
              />
            </div>
          )}

          <Controller
            name={`Drugs.${index}.Times`}
            control={control}
            render={({ field: timesField }) => {
              const selectedOption = timesOptions.find(
                (option) => String(option.value) === String(timesField.value),
              );
              return (
                <GeneralSelect
                  name={timesField.name}
                  placeholder={t("times")}
                  options={timesOptions}
                  value={(selectedOption as any) || undefined}
                  onChange={(option: any) =>
                    timesField.onChange(option?.value || "")
                  }
                  label={t("times")}
                />
              );
            }}
          />

          <div className="relative">
            <GeneralInput
              label={t("duration")}
              placeholder={t("duration")}
              customStyleClasses={"!min-w-full"}
              name={`Drugs.${index}.Duration`}
              maxLength={3}
              type="number"
              numericOnly
            />
            <div className="absolute end-1 top-0 m-2 flex h-10 items-center rounded-lg bg-white px-3">
              {t("days")}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="mb-3 flex flex-col items-end justify-between gap-6 lg:flex-row">
            <Controller
              name={`Drugs.${index}.Frequency`}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <h2>{t("frequency")}</h2>
                  <div className="flex flex-1 flex-wrap gap-3">
                    {frequencyTypeOptions.map((o) => (
                      <GeneralRadio
                        key={o.key}
                        {...field}
                        value={o.value}
                        label={o.label}
                        checked={field.value === o.value}
                        onClick={() => field.onChange(o.value)}
                      />
                    ))}
                  </div>
                </div>
              )}
            />

            <div>
              <Controller
                name={`Drugs.${index}.Meal`}
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <h2>{t("period")}</h2>
                    <div className="flex flex-wrap gap-2">
                      {mealTypeOptions.map((option) => (
                        <GeneralRadio
                          key={option.key}
                          name={field.name}
                          label={option.label}
                          value={option.value}
                          checked={field.value === option.value}
                          onClick={() =>
                            field.onChange(
                              field.value === option.value ? "" : option.value,
                            )
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="flex flex-col gap-2">
              <h2>{t("sleep")}</h2>
              <GeneralCheckbox
                name={`Drugs.${index}.Sleep`}
                label={t("before_sleep")}
                defaultChecked={field.Sleep || false}
                inputClasses="appointment-checkbox !w-[1.2rem] !h-[1.2rem] !rounded-md"
              />
            </div>
          </div>

          <Controller
            name={`Drugs.${index}.Instructions`}
            control={control}
            render={({ field }) => (
              <GeneralInput
                type="textarea"
                {...field}
                label={t("instructions")}
                placeholder={t("instructions")}
                maxLength={maxInstructionsLength}
              />
            )}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default PrescriptionDrugItem;
